/**
 * OAuthPanel
 *
 * Capability-keyed setup content rendered when a connector declares the
 * `oauth` capability. Shows a short help line explaining the redirect flow;
 * for store-URL based providers (Tiendanube, Shopify, Fenicio) it also
 * collects the store URL inline.
 *
 * Purely presentational — the action is driven by the modal footer
 * (`AddChannelModal`), domain state, dispatches, and the actual redirect are
 * owned by the caller. This keeps the panel reusable across capability-aware
 * setup surfaces.
 */

import { Stack, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { integrationsTestIds } from '@domains/integrations/testids';
import styles from './SetupPanel.module.css';

export type OAuthPanelProps = {
  /** Connector id, used for the data-testid (e.g. `mercadolibre`, `tiendanube`). */
  connectorId: string;
  /** Human-readable connector label, used inside the body copy. */
  connectorLabel: string;
  /**
   * Whether this connector needs a store URL collected before redirect
   * (TiendaNube, Shopify, Fenicio). When `true`, `storeUrl` + `onStoreUrlChange`
   * MUST be provided.
   */
  requiresStoreUrl?: boolean;
  /** Controlled store URL value (only used when `requiresStoreUrl`). */
  storeUrl?: string;
  /** Controlled store URL change handler. */
  onStoreUrlChange?: ( value: string ) => void;
  /** Placeholder shown in the store-URL input. */
  storeUrlPlaceholder?: string;
};

const OAuthPanel = ({
  connectorId,
  connectorLabel,
  requiresStoreUrl = false,
  storeUrl,
  onStoreUrlChange,
  storeUrlPlaceholder
}: OAuthPanelProps ) => {
  const { t } = useTranslation( 'integrations' );

  return (
    <Stack gap="sm">
      <Text className={styles.helpText}>
        {t(
          'setup_panel.oauth.help',
          'Te redirigiremos a {{connector}} para autorizar la conexión de forma segura.',
          { connector: connectorLabel }
        )}
      </Text>

      {requiresStoreUrl && (
        <TextInput
          size="sm"
          radius="xs"
          label={t( 'setup_panel.oauth.store_url_label', 'URL de la tienda' )}
          value={storeUrl ?? ''}
          onChange={( e ) => onStoreUrlChange?.( e.currentTarget.value )}
          onPaste={( e ) => {
            e.preventDefault();
            const pasted = e.clipboardData.getData( 'text' );
            onStoreUrlChange?.( pasted.trim().replace( /^https?:\/\//, '' ));
          }}
          placeholder={storeUrlPlaceholder ?? 'mitienda.example.com'}
          leftSection={<Text size="13px">https://</Text>}
          leftSectionWidth={70}
          data-testid={integrationsTestIds.setupOauthStoreUrl( connectorId )}
        />
      )}
    </Stack>
  );
};

export default OAuthPanel;
